import { useEffect, useRef, useState } from "react";
import { useTripStore } from "../store/tripStore";
import { useLibraryStore } from "../store/libraryStore";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { formatDateRange } from "../utils/format";
import {
  addCalendarDays,
  addDaysToDate,
  daysBetweenDates,
  zonedDate,
} from "../utils/zonedTime";

interface DroppedBlock {
  id: string;
  name: string;
  /** Date (after any start shift) the block sits on — outside the new range */
  date: string;
  kind: "poi" | "note";
}

interface PendingRange {
  startDate: string;
  endDate: string;
  dropped: DroppedBlock[];
}

/**
 * Start–end date-range pill in the shell header — the only way trip length
 * changes (days are not entities). Moving the start shifts every block's
 * datetime with it; shrinking the range confirms before deleting the blocks
 * on dropped dates. Locked blocks veto shifts and shrinks via the store's
 * locked-action prompt.
 */
export function TripDateRangePicker({
  firstDate,
  lastDate,
}: {
  firstDate: string;
  lastDate: string;
}) {
  const setTripDateRange = useTripStore((s) => s.setTripDateRange);
  const schedule = useTripStore((s) => s.schedule);
  const isReadOnly = useTripStore((s) => s.isReadOnly);
  const pois = useLibraryStore((s) => s.pois);

  const [isEditing, setIsEditing] = useState(false);
  const [range, setRange] = useState({ start: firstDate, end: lastDate });
  const [pending, setPending] = useState<PendingRange | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const startInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) {
      setRange({ start: firstDate, end: lastDate });
    }
  }, [firstDate, lastDate, isEditing]);

  useEffect(() => {
    if (isEditing) startInputRef.current?.focus();
  }, [isEditing]);

  /** Blocks that would fall outside the new range (after any start shift). */
  const analyzeDropped = (startDate: string, endDate: string) => {
    const deltaDays = daysBetweenDates(firstDate, startDate);
    const shiftedDate = (value: string) =>
      zonedDate(deltaDays === 0 ? value : addCalendarDays(value, deltaDays));
    const outside = (date: string) => date < startDate || date > endDate;

    const dropped: DroppedBlock[] = [];
    let droppedLocked = false;

    for (const sp of schedule.scheduledPOIs) {
      const date = shiftedDate(sp.startTime);
      if (!outside(date)) continue;
      const poi = pois.find((p) => p.id === sp.poiId);
      dropped.push({
        id: sp.id,
        name: poi?.name ?? "Unknown place",
        date,
        kind: "poi",
      });
      droppedLocked ||= sp.locked;
    }
    for (const note of schedule.notes) {
      if (note.startTime) {
        const date = shiftedDate(note.startTime);
        if (!outside(date)) continue;
        dropped.push({ id: note.id, name: note.name, date, kind: "note" });
        droppedLocked ||= note.locked;
        continue;
      }
      if (note.allDayStartDate && note.allDayEndDate) {
        const start =
          deltaDays === 0
            ? note.allDayStartDate
            : addDaysToDate(note.allDayStartDate, deltaDays);
        const end =
          deltaDays === 0
            ? note.allDayEndDate
            : addDaysToDate(note.allDayEndDate, deltaDays);
        if (!outside(start) && !outside(end)) continue;
        dropped.push({
          id: note.id,
          name: note.name,
          date: outside(start) ? start : end,
          kind: "note",
        });
        droppedLocked ||= note.locked;
      }
    }
    dropped.sort((a, b) => a.date.localeCompare(b.date));

    // Mirrors the store's guards: it refuses (with the locked prompt) when a
    // start shift would move locked blocks, or a shrink would delete one.
    const hasLockedBlocks =
      schedule.scheduledPOIs.some((sp) => sp.locked) ||
      schedule.notes.some(
        (n) => n.locked && (n.startTime || n.allDayStartDate),
      );
    const storeWillBlock =
      (deltaDays !== 0 && hasLockedBlocks) || droppedLocked;

    return { dropped, storeWillBlock };
  };

  const handleCommit = () => {
    setIsEditing(false);
    const { start, end } = range;
    if (!start || !end || start > end) return;
    if (start === firstDate && end === lastDate) return;

    const { dropped, storeWillBlock } = analyzeDropped(start, end);
    if (dropped.length > 0 && !storeWillBlock) {
      // Shrink deletes blocks — confirm with the affected list first
      setPending({ startDate: start, endDate: end, dropped });
      return;
    }
    // Applies directly, or no-ops with the locked-action prompt
    setTripDateRange(start, end);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleCommit();
    } else if (e.key === "Escape") {
      setRange({ start: firstDate, end: lastDate });
      setIsEditing(false);
    }
  };

  const confirmPending = () => {
    if (pending) setTripDateRange(pending.startDate, pending.endDate);
    setPending(null);
  };

  return (
    <>
      {isEditing ? (
        <div
          ref={editorRef}
          className="flex items-center gap-1.5"
          onBlur={(e) => {
            // Commit once focus leaves the whole range editor
            if (!editorRef.current?.contains(e.relatedTarget as Node)) {
              handleCommit();
            }
          }}
        >
          <input
            ref={startInputRef}
            type="date"
            value={range.start}
            max={range.end}
            onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
            onKeyDown={handleKeyDown}
            aria-label="Trip start date"
            className="input-base w-36 px-2 py-1 text-sm font-medium"
          />
          <span className="text-sm text-tertiary">–</span>
          <input
            type="date"
            value={range.end}
            min={range.start}
            onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
            onKeyDown={handleKeyDown}
            aria-label="Trip end date"
            className="input-base w-36 px-2 py-1 text-sm font-medium"
          />
        </div>
      ) : (
        <button
          onClick={() => !isReadOnly() && setIsEditing(true)}
          disabled={isReadOnly()}
          className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-secondary transition-colors ${
            isReadOnly()
              ? "bg-subtle cursor-default"
              : "bg-subtle hover:bg-border"
          }`}
          title={isReadOnly() ? "Read-only mode" : "Click to edit trip dates"}
        >
          <span className="material-symbols-outlined text-base text-tertiary">
            calendar_today
          </span>
          <span>{formatDateRange(firstDate, lastDate)}</span>
          <span className="material-symbols-outlined text-sm text-tertiary reveal-on-card-hover">
            edit
          </span>
        </button>
      )}

      {/* Shrink confirmation: lists the blocks on dropped dates */}
      <Modal
        open={pending !== null}
        onClose={() => setPending(null)}
        title="Remove scheduled items?"
        icon="event_busy"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmPending}>
              Remove {pending?.dropped.length} and update dates
            </Button>
          </div>
        }
      >
        <p className="text-sm text-secondary">
          These dates fall outside{" "}
          <span className="font-semibold text-primary">
            {pending && formatDateRange(pending.startDate, pending.endDate)}
          </span>
          . The following will be removed from the schedule:
        </p>
        <ul className="mt-3 flex flex-col gap-1.5">
          {pending?.dropped.map((block) => (
            <li
              key={`${block.kind}-${block.id}`}
              className="flex items-center gap-2 rounded-lg border border-border bg-subtle px-2.5 py-1.5 text-sm"
            >
              <span
                className="material-symbols-outlined text-base text-tertiary"
                aria-hidden
              >
                {block.kind === "poi" ? "location_on" : "sticky_note_2"}
              </span>
              <span className="min-w-0 flex-1 truncate font-semibold">
                {block.name}
              </span>
              <span className="whitespace-nowrap text-xs text-tertiary tabular-nums">
                {block.date}
              </span>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}
